import { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Play, 
  Trash2, 
  FileImage,
  Video,
  CheckCircle,
  AlertCircle,
  Loader2,
  AlertTriangle,
  ArrowRight,
  FolderOpen,
  FolderOutput,
  FileEdit,
  Clock
} from 'lucide-react';
import FeatureGuard from '../components/FeatureGuard';

function VideoMatchContent() {
  const [imageFolder, setImageFolder] = useState(null);
  const [videoFolder, setVideoFolder] = useState(null);
  const [outputFolder, setOutputFolder] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [uniqueImages, setUniqueImages] = useState([]); // Benzersiz barkodlu resimler
  const [processing, setProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [results, setResults] = useState(null);
  const [renameInPlace, setRenameInPlace] = useState(false);

  // Check if File System Access API is supported
  const isSupported = 'showDirectoryPicker' in window;

  const extractBarcode = (filename) => {
    // Supports single or hyphen-joined multi-barcodes:
    //  8690200090523_(0035).jpg -> 8690200090523
    //  8690200091810-8690200088186_(0035).jpg -> 8690200091810-8690200088186
    const match = filename.match(/^(\d+(?:-\d+)*)_\(/);
    return match ? match[1] : null;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleSelectImageFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor. Chrome veya Edge kullanın.');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({
        mode: 'read'
      });

      setImageFolder(dirHandle);

      // Read all image files from directory (including RAW formats)
      const files = [];
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.cr2', '.raf', '.raw', '.nef', '.arw'];
      
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const ext = entry.name.toLowerCase().slice(entry.name.lastIndexOf('.'));
          if (imageExtensions.includes(ext)) {
            const file = await entry.getFile();
            const barcode = extractBarcode(entry.name);
            files.push({
              name: entry.name,
              file,
              handle: entry,
              lastModified: file.lastModified,
              barcode: barcode
            });
          }
        }
      }

      if (files.length === 0) {
        toast.error('Klasörde resim dosyası bulunamadı');
        return;
      }

      // Sort by creation/modification date (oldest first)
      files.sort((a, b) => a.lastModified - b.lastModified);
      
      // Filter unique barcodes - keep only FIRST occurrence of each barcode
      // (Her barkoddan 3 resim var, sadece ilkini al)
      const seenBarcodes = new Set();
      const uniqueList = [];
      
      for (const img of files) {
        if (img.barcode && !seenBarcodes.has(img.barcode)) {
          uniqueList.push({
            ...img,
            orderIndex: uniqueList.length + 1 // Sıra numarası
          });
          seenBarcodes.add(img.barcode);
        }
      }
      
      setImageFiles(files);
      setUniqueImages(uniqueList);
      setPreviewData(null);
      setResults(null);
      
      const skippedCount = files.length - uniqueList.length;
      toast.success(`${files.length} resim bulundu, ${uniqueList.length} benzersiz barkod (tarihe göre sıralandı)`);

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Klasör seçme hatası:', err);
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handleSelectVideoFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor. Chrome veya Edge kullanın.');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({
        mode: renameInPlace ? 'readwrite' : 'read'
      });

      setVideoFolder(dirHandle);

      // Read all video files from directory
      const files = [];
      const videoExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v'];
      
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const ext = entry.name.toLowerCase().slice(entry.name.lastIndexOf('.'));
          if (videoExtensions.includes(ext)) {
            const file = await entry.getFile();
            files.push({
              name: entry.name,
              file,
              handle: entry,
              lastModified: file.lastModified,
              ext: ext
            });
          }
        }
      }

      if (files.length === 0) {
        toast.error('Klasörde video dosyası bulunamadı');
        return;
      }

      // Sort by creation/modification date (oldest first)
      files.sort((a, b) => a.lastModified - b.lastModified);
      
      // Add order index
      files.forEach((f, idx) => {
        f.orderIndex = idx + 1;
      });
      
      setVideoFiles(files);
      setPreviewData(null);
      setResults(null);
      toast.success(`${files.length} video bulundu (tarihe göre sıralandı)`);

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Klasör seçme hatası:', err);
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handleSelectOutputFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor. Chrome veya Edge kullanın.');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite'
      });

      setOutputFolder(dirHandle);
      setPreviewData(null);
      setResults(null);
      toast.success(`Çıktı klasörü seçildi: ${dirHandle.name}`);

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Klasör seçme hatası:', err);
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handlePreview = useCallback(() => {
    if (uniqueImages.length === 0 || videoFiles.length === 0) {
      toast.error('Lütfen hem resim hem de video klasörlerini seçin');
      return;
    }

    if (!renameInPlace && !outputFolder) {
      toast.error('Lütfen çıktı klasörünü seçin veya "Videonun Adını Değiştir" seçeneğini aktifleştirin');
      return;
    }

    // Check counts
    const warnings = [];
    if (videoFiles.length > uniqueImages.length) {
      warnings.push(`Video sayısı (${videoFiles.length}) benzersiz barkod sayısından (${uniqueImages.length}) fazla!`);
    }

    // Create matches - by order (both sorted by creation date)
    // Video 1 (oldest) matches with Image 1 (oldest unique barcode)
    const matches = [];
    const matchCount = Math.min(videoFiles.length, uniqueImages.length);
    
    for (let i = 0; i < matchCount; i++) {
      const video = videoFiles[i];
      const image = uniqueImages[i];
      const newName = `${image.barcode}${video.ext}`;
      
      matches.push({
        index: i + 1,
        videoIndex: i,
        imageIndex: i,
        video: video.name,
        videoDate: video.lastModified,
        image: image.name,
        imageDate: image.lastModified,
        barcode: image.barcode,
        new_name: newName
      });
    }

    const preview = {
      total_images: imageFiles.length,
      unique_barcodes: uniqueImages.length,
      total_videos: videoFiles.length,
      matches,
      warnings,
      can_process: matchCount > 0 && videoFiles.length <= uniqueImages.length
    };

    setPreviewData(preview);
    
    if (warnings.length > 0) {
      toast.warning(warnings[0]);
    } else {
      toast.success(`${matches.length} eşleşme hazır`);
    }
  }, [imageFiles, uniqueImages, videoFiles, outputFolder, renameInPlace]);

  const handleProcess = async () => {
    if (!previewData || !previewData.can_process) {
      toast.error('Lütfen önce önizleme yapın');
      return;
    }

    if (!renameInPlace && !outputFolder) {
      toast.error('Lütfen çıktı klasörünü seçin');
      return;
    }

    setProcessing(true);
    const processResults = {
      success: [],
      errors: [],
      summary: {
        total_videos: videoFiles.length,
        matched: 0
      }
    };

    try {
      if (renameInPlace) {
        try {
          const permission = await videoFolder.requestPermission({ mode: 'readwrite' });
          if (permission !== 'granted') {
            toast.error('Klasör yazma izni gerekli');
            setProcessing(false);
            return;
          }
        } catch (err) {
          console.error('Permission error:', err);
        }
      }

      for (const match of previewData.matches) {
        const video = videoFiles[match.videoIndex];
        
        try {
          if (renameInPlace) {
            const file = await video.handle.getFile();
            const content = await file.arrayBuffer();
            
            const newHandle = await videoFolder.getFileHandle(match.new_name, { create: true });
            const writable = await newHandle.createWritable();
            await writable.write(content);
            await writable.close();
            
            if (video.name !== match.new_name) {
              await videoFolder.removeEntry(video.name);
            }
            
            processResults.success.push({
              original: video.name,
              new_name: match.new_name,
              barcode: match.barcode,
              action: 'renamed'
            });
          } else {
            const file = await video.handle.getFile();
            const content = await file.arrayBuffer();
            
            const newHandle = await outputFolder.getFileHandle(match.new_name, { create: true });
            const writable = await newHandle.createWritable();
            await writable.write(content);
            await writable.close();
            
            processResults.success.push({
              original: video.name,
              new_name: match.new_name,
              barcode: match.barcode,
              action: 'copied'
            });
          }
          
          processResults.summary.matched++;
        } catch (err) {
          console.error(`Error processing ${video.name}:`, err);
          processResults.errors.push(`${video.name}: ${err.message}`);
        }
      }

      setResults(processResults);
      
      if (processResults.errors.length === 0) {
        const action = renameInPlace ? 'yeniden adlandırıldı' : 'kopyalandı';
        toast.success(`İşlem tamamlandı! ${processResults.summary.matched} video ${action}.`);
      } else {
        toast.warning(`İşlem tamamlandı ancak ${processResults.errors.length} hata oluştu.`);
      }

    } catch (err) {
      console.error('Process error:', err);
      toast.error('İşlem sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const handleClear = () => {
    setImageFolder(null);
    setVideoFolder(null);
    setOutputFolder(null);
    setImageFiles([]);
    setVideoFiles([]);
    setUniqueImages([]);
    setPreviewData(null);
    setResults(null);
  };

  const handleRenameInPlaceChange = (checked) => {
    setRenameInPlace(checked);
    if (videoFolder) {
      setVideoFolder(null);
      setVideoFiles([]);
      setPreviewData(null);
      setResults(null);
    }
  };

  if (!isSupported) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Tarayıcı Desteklenmiyor
            </CardTitle>
          </CardHeader>
          <CardContent className="text-red-700">
            <p>Bu özellik için File System Access API gereklidir.</p>
            <p className="mt-2">Lütfen <strong>Google Chrome</strong> veya <strong>Microsoft Edge</strong> tarayıcısını kullanın.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="video-match-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Video Eşleştir
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Resim dosyalarındaki barkodları kullanarak videoları yeniden adlandırın
        </p>
      </div>

      {/* Instructions Card */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-blue-800">Nasıl Çalışır?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-700 space-y-1">
          <p><strong>1.</strong> Resim klasörü seçilir (format: <code className="bg-blue-100 px-1 rounded">8690200090523_(0035).jpg</code>)</p>
          <p><strong>2.</strong> Video klasörü seçilir</p>
          <p><strong>3.</strong> Dosyalar <strong>yaratılış tarihine göre</strong> eskiden yeniye sıralanır</p>
          <p><strong>4.</strong> Her barkodun 3 resminden sadece <strong>ilki</strong> alınır (2'si atlanır)</p>
          <p><strong>5.</strong> Sırayla eşleştirilir: 1. video → 1. barkod, 2. video → 2. barkod...</p>
          <div className="mt-3 p-2 bg-blue-100 rounded-lg text-xs">
            <strong>Desteklenen Formatlar:</strong><br/>
            📷 Resim: JPG, JPEG, PNG, GIF, BMP, <strong>CR2, RAF</strong>, RAW, NEF, ARW<br/>
            🎬 Video: MP4, AVI, MOV, MKV, WMV, FLV, WEBM, M4V
          </div>
        </CardContent>
      </Card>

      {/* Rename In Place Option */}
      <Card className="mb-6 border-orange-200 bg-orange-50">
        <CardContent className="pt-4">
          <div className="flex items-center space-x-3">
            <Checkbox 
              id="renameInPlace" 
              checked={renameInPlace}
              onCheckedChange={handleRenameInPlaceChange}
              data-testid="rename-in-place-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="renameInPlace" className="text-base font-medium text-orange-800 cursor-pointer flex items-center gap-2">
                <FileEdit className="w-4 h-4" />
                Videonun Adını Değiştir (Kopyalama Yapma)
              </Label>
              <p className="text-xs text-orange-600 mt-1">
                {renameInPlace 
                  ? '✓ Aktif: Orijinal video dosyalarının adı değiştirilecek'
                  : '○ Pasif: Videolar çıktı klasörüne kopyalanacak'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Images Folder Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileImage className="w-5 h-5 text-green-600" />
              Resim Klasörü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleSelectImageFolder}
              variant="outline"
              className="w-full h-20 border-2 border-dashed border-gray-300 hover:border-green-500 transition-colors bg-gray-50 flex flex-col items-center justify-center gap-1"
              data-testid="select-image-folder-btn"
            >
              <FolderOpen className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600">
                {imageFolder ? imageFolder.name : 'Klasör Seç'}
              </span>
            </Button>
            
            {imageFiles.length > 0 && (
              <div className="mt-3 p-2 bg-green-50 rounded-lg text-xs">
                <div className="font-medium text-green-800 mb-1">
                  {imageFiles.length} resim → {uniqueImages.length} benzersiz barkod
                </div>
                <div className="text-green-600">
                  ({imageFiles.length - uniqueImages.length} tekrar atlandı)
                </div>
              </div>
            )}
            
            {uniqueImages.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg p-2 text-xs bg-white">
                <div className="text-gray-500 mb-1 font-medium flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Benzersiz Barkodlar (tarihe göre sıralı):
                </div>
                {uniqueImages.slice(0, 8).map((item, idx) => (
                  <div key={idx} className="text-gray-700 py-0.5 flex items-center gap-2">
                    <span className="bg-green-100 text-green-700 px-1.5 rounded text-[10px] font-mono">{idx + 1}</span>
                    <span className="font-mono">{item.barcode}</span>
                    <span className="text-gray-400 text-[10px]">{formatDate(item.lastModified)}</span>
                  </div>
                ))}
                {uniqueImages.length > 8 && (
                  <div className="text-gray-400 mt-1">... +{uniqueImages.length - 8} barkod daha</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Videos Folder Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Video className="w-5 h-5 text-blue-600" />
              Video Klasörü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleSelectVideoFolder}
              variant="outline"
              className="w-full h-20 border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors bg-gray-50 flex flex-col items-center justify-center gap-1"
              data-testid="select-video-folder-btn"
            >
              <FolderOpen className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600">
                {videoFolder ? videoFolder.name : 'Klasör Seç'}
              </span>
            </Button>
            
            {videoFiles.length > 0 && (
              <div className="mt-3 p-2 bg-blue-50 rounded-lg text-xs">
                <div className="font-medium text-blue-800">
                  {videoFiles.length} video bulundu
                </div>
              </div>
            )}
            
            {videoFiles.length > 0 && (
              <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg p-2 text-xs bg-white">
                <div className="text-gray-500 mb-1 font-medium flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Videolar (tarihe göre sıralı):
                </div>
                {videoFiles.slice(0, 8).map((item, idx) => (
                  <div key={idx} className="text-gray-700 py-0.5 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-700 px-1.5 rounded text-[10px] font-mono">{idx + 1}</span>
                    <span className="truncate">{item.name}</span>
                    <span className="text-gray-400 text-[10px]">{formatDate(item.lastModified)}</span>
                  </div>
                ))}
                {videoFiles.length > 8 && (
                  <div className="text-gray-400 mt-1">... +{videoFiles.length - 8} video daha</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Output Folder Selection */}
        <Card className={renameInPlace ? 'opacity-50' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderOutput className="w-5 h-5 text-purple-600" />
              Çıktı Klasörü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleSelectOutputFolder}
              variant="outline"
              disabled={renameInPlace}
              className="w-full h-20 border-2 border-dashed border-gray-300 hover:border-purple-500 transition-colors bg-gray-50 flex flex-col items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="select-output-folder-btn"
            >
              <FolderOutput className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600">
                {renameInPlace ? 'Devre dışı' : (outputFolder ? outputFolder.name : 'Klasör Seç')}
              </span>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          onClick={handlePreview}
          disabled={uniqueImages.length === 0 || videoFiles.length === 0 || (!renameInPlace && !outputFolder)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="preview-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          Önizle
        </Button>

        <Button
          onClick={handleProcess}
          disabled={!previewData?.can_process || processing}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          data-testid="process-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              İşleniyor...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              {renameInPlace ? 'Adları Değiştir' : 'Kopyala'}
            </>
          )}
        </Button>

        <Button
          onClick={handleClear}
          variant="outline"
          className="text-red-600 border-red-300 hover:bg-red-50"
          data-testid="clear-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Temizle
        </Button>
      </div>

      {/* Preview Results */}
      {previewData && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Eşleştirme Önizlemesi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="bg-gray-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-gray-800">{previewData.total_images}</div>
                <div className="text-xs text-gray-500">Toplam Resim</div>
              </div>
              <div className="bg-green-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-green-600">{previewData.unique_barcodes}</div>
                <div className="text-xs text-green-700">Benzersiz Barkod</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-blue-600">{previewData.total_videos}</div>
                <div className="text-xs text-blue-700">Toplam Video</div>
              </div>
              <div className="bg-purple-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-purple-600">{previewData.matches.length}</div>
                <div className="text-xs text-purple-700">Eşleşme</div>
              </div>
            </div>

            {/* Warnings */}
            {previewData.warnings.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex items-center gap-2 text-yellow-800 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Uyarılar</span>
                </div>
                {previewData.warnings.map((warning, idx) => (
                  <div key={idx} className="text-sm text-yellow-700">{warning}</div>
                ))}
              </div>
            )}

            {/* Match List */}
            {previewData.matches.length > 0 && (
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 text-xs font-medium text-gray-600">
                  Sıra | Video → Yeni Ad (Barkod kaynağı)
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {previewData.matches.map((match, idx) => (
                    <div key={idx} className="flex items-center gap-3 px-4 py-2 border-t hover:bg-gray-50">
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-bold min-w-[28px] text-center">
                        {match.index}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 truncate">{match.video}</span>
                          <ArrowRight className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm font-bold text-green-700 font-mono">{match.new_name}</span>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          Barkod kaynağı: {match.image}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Can Process Indicator */}
            <div className={`mt-4 p-3 rounded-xl flex items-center gap-2 ${previewData.can_process ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {previewData.can_process ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>
                    İşleme hazır! "{renameInPlace ? 'Adları Değiştir' : 'Kopyala'}" butonuna tıklayın.
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5" />
                  <span>İşlem yapılamaz. Lütfen uyarıları kontrol edin.</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Processing Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              İşlem Sonuçları
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-green-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-green-600">{results.summary.matched}</div>
                <div className="text-xs text-green-700">Başarılı</div>
              </div>
              <div className="bg-red-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-red-600">{results.errors.length}</div>
                <div className="text-xs text-red-700">Hata</div>
              </div>
              <div className="bg-purple-50 p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-purple-600">{results.summary.total_videos}</div>
                <div className="text-xs text-purple-700">Toplam</div>
              </div>
            </div>

            {results.success.length > 0 && (
              <div className="border rounded-xl overflow-hidden">
                <div className="max-h-40 overflow-y-auto">
                  {results.success.slice(0, 20).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-4 py-2 border-b last:border-b-0 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-600 truncate">{item.original}</span>
                      <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-green-700 font-medium font-mono">{item.new_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {results.errors.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2 text-red-800 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Hatalar</span>
                </div>
                {results.errors.map((error, idx) => (
                  <div key={idx} className="text-sm text-red-700">{error}</div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// Wrapper with FeatureGuard
export default function VideoMatch() {
  return (
    <FeatureGuard featureKey="video_match" pageName="Video Eşleştir">
      <VideoMatchContent />
    </FeatureGuard>
  );
}
